import React, { useState, useEffect } from 'react';
import { getExpenses, deleteExpense, bulkDeleteExpenses, updateExpense, getExpenseById, getReceiptImage, rotateReceiptImage, getSpendingTrends } from '../services/api';
import { getUserProfile, getSupportedCurrencies } from '../services/settingsApi';
import { formatDateDisplay } from '../utils/date';
import { exportToCSV, exportToPDF, getFormattedFilename } from '../utils/exportUtils';
import { formatCurrency, formatNumber } from '../utils/numberFormat';
import { logErrorToBackend } from '../utils/errorLogger';

function ExpenseList({ refreshTrigger, newExpenseIds = new Set(), onClearNewExpenseIds }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);

  // Filter states
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('current-month');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');

  // Sort state
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [selectedExpenses, setSelectedExpenses] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(null);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  // Bulk edit states
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditField, setBulkEditField] = useState('category');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showAllExpenses, setShowAllExpenses] = useState(false);
  const pageSize = 100;

  // Currency state
  const [userDefaultCurrency, setUserDefaultCurrency] = useState('SGD');

  // Receipt image state
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const [receiptImageLoading, setReceiptImageLoading] = useState(false);
  const [imageRotation, setImageRotation] = useState(0); // Current visual rotation
  const [isSavingRotation, setIsSavingRotation] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const categories = ['all', 'groceries', 'dining', 'transportation', 'entertainment', 'shopping', 'utilities', 'healthcare', 'other'];

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate date range based on timeRange
  const getDateRange = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // Handle specific month selections like "2026-02"
    if (/^\d{4}-\d{2}$/.test(timeRange)) {
      const [y, m] = timeRange.split('-').map(Number);
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      return { start: formatDateLocal(start), end: formatDateLocal(end) };
    }

    switch (timeRange) {
      case 'current-month': {
        const startOfMonth = new Date(currentYear, currentMonth, 1);
        const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
        return { start: formatDateLocal(startOfMonth), end: formatDateLocal(endOfMonth) };
      }
      case 'this-year': {
        const startOfYear = new Date(currentYear, 0, 1);
        const endOfYear = new Date(currentYear, 11, 31);
        return { start: formatDateLocal(startOfYear), end: formatDateLocal(endOfYear) };
      }
      case 'last-year': {
        const startOfYear = new Date(currentYear - 1, 0, 1);
        const endOfYear = new Date(currentYear - 1, 11, 31);
        return { start: formatDateLocal(startOfYear), end: formatDateLocal(endOfYear) };
      }
      case 'all-time':
      default:
        return { start: '', end: '' };
    }
  };

  const dateRange = getDateRange();

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchExpenses(1);
  }, [categoryFilter, sourceFilter, currencyFilter, timeRange, customStartDate, customEndDate, minAmount, maxAmount, vendorSearch, sortBy, sortOrder, refreshTrigger]);

  // Fetch when currentPage changes
  useEffect(() => {
    fetchExpenses(currentPage);
  }, [currentPage]);

  // Load user's default currency on mount
  useEffect(() => {
    const loadUserCurrency = async () => {
      try {
        const profileData = await getUserProfile();
        if (profileData.profile?.default_currency_code) {
          setUserDefaultCurrency(profileData.profile.default_currency_code);
        }
      } catch (err) {
        console.error('Failed to load user currency:', err);
        // Fall back to SGD
        setUserDefaultCurrency('SGD');
      }
    };
    loadUserCurrency();
  }, []);

  // Load supported currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await getSupportedCurrencies();
        console.log('📊 [ExpenseList] getSupportedCurrencies response:', data);
        console.log('📊 [ExpenseList] data.currencies type:', typeof data.currencies, ', isArray:', Array.isArray(data.currencies));

        if (data.currencies && Array.isArray(data.currencies)) {
          console.log('✅ [ExpenseList] Valid currencies array with', data.currencies.length, 'items');
          setCurrencies(data.currencies);
        } else {
          console.warn('⚠️ [ExpenseList] data.currencies is not a valid array:', data.currencies);
          setCurrencies([]);
        }
      } catch (err) {
        console.error('❌ [ExpenseList] Failed to load currencies:', err);
        setCurrencies([]);
      }
    };
    loadCurrencies();
  }, []);

  // Fetch months that have recorded expenses
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const result = await getSpendingTrends(null, null, 'monthly');
        const months = (result.data || [])
          .map(t => t.date || t.month)
          .filter(Boolean)
          .sort((a, b) => b.localeCompare(a));
        setAvailableMonths(months);
      } catch (err) {
        console.error('Failed to load available months:', err);
      }
    };
    fetchMonths();
  }, []);

  // Track mobile viewport for responsive rendering
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Human-readable label for the current time range
  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formatPeriodLabel = (dateStr) => {
    const d = new Date(dateStr + '-01');
    return `${MONTH_NAMES[d.getMonth()]}-${String(d.getFullYear()).slice(2)}`;
  };
  const getTimeRangeLabel = () => {
    const today = new Date();
    const cy = today.getFullYear();
    if (/^\d{4}-\d{2}$/.test(timeRange)) return formatPeriodLabel(timeRange);
    switch (timeRange) {
      case 'current-month': return formatPeriodLabel(`${cy}-${String(today.getMonth() + 1).padStart(2, '0')}`);
      case 'this-year': return `${cy}`;
      case 'last-year': return `${cy - 1}`;
      case 'all-time':
      default: return 'All Time';
    }
  };

  // Auto-clear NEW badges after 30 seconds
  useEffect(() => {
    if (newExpenseIds.size > 0 && onClearNewExpenseIds) {
      const timer = setTimeout(() => {
        onClearNewExpenseIds();
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [newExpenseIds, onClearNewExpenseIds]);

  // Fetch receipt image when expense is expanded
  useEffect(() => {
    const fetchReceiptImageUrl = async () => {
      // Reset rotation when changing expense
      setImageRotation(0);

      if (!expandedId) {
        setReceiptImageUrl(null);
        return;
      }

      const expense = expenses.find(e => e.id === expandedId);
      if (!expense?.receipt_image_url) {
        setReceiptImageUrl(null);
        return;
      }

      setReceiptImageLoading(true);
      try {
        const response = await getReceiptImage(expandedId);
        setReceiptImageUrl(response.imageUrl);
      } catch (err) {
        console.error('Failed to load receipt image:', err);
        setReceiptImageUrl(null);
      } finally {
        setReceiptImageLoading(false);
      }
    };

    fetchReceiptImageUrl();
  }, [expandedId, expenses]);

  // Handle image rotation
  const handleRotateImage = (direction) => {
    setImageRotation(prev => {
      const newRotation = direction === 'cw'
        ? (prev + 90) % 360
        : (prev - 90 + 360) % 360;
      return newRotation;
    });
  };

  // Save rotated image
  const handleSaveRotation = async () => {
    if (imageRotation === 0 || !expandedId) return;

    setIsSavingRotation(true);
    try {
      await rotateReceiptImage(expandedId, imageRotation);
      // Refresh the image to show the saved rotation
      const response = await getReceiptImage(expandedId);
      setReceiptImageUrl(response.imageUrl + '?t=' + Date.now()); // Cache bust
      setImageRotation(0); // Reset visual rotation since it's now saved
    } catch (err) {
      console.error('Failed to save rotated image:', err);
      alert('Failed to save rotated image: ' + err.message);
    } finally {
      setIsSavingRotation(false);
    }
  };

  const fetchExpenses = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const offset = (page - 1) * pageSize;
      const filters = {
        limit: pageSize,
        offset: offset,
        sortBy,
        sortOrder
      };

      if (categoryFilter !== 'all') {
        filters.category = categoryFilter;
      }
      if (currencyFilter !== 'all') {
        filters.currency = currencyFilter;
      }
      if (dateRange.start) {
        filters.startDate = dateRange.start;
      }
      if (dateRange.end) {
        filters.endDate = dateRange.end;
      }
      if (minAmount) {
        filters.minAmount = parseFloat(minAmount);
      }
      if (maxAmount) {
        filters.maxAmount = parseFloat(maxAmount);
      }
      if (vendorSearch) {
        filters.merchant = vendorSearch;
      }

      const response = await getExpenses(filters);
      const fetchedExpenses = response.expenses || [];

      // Pin newly added expenses to the top of page 1 so the user sees them immediately
      if (newExpenseIds.size > 0 && page === 1) {
        const fetchedIds = new Set(fetchedExpenses.map(e => e.id));
        // Fetch any new expenses that aren't in the current page results
        const missingIds = [...newExpenseIds].filter(id => !fetchedIds.has(id));
        const fetched = await Promise.all(
          missingIds.map(id => getExpenseById(id).catch(() => null))
        );
        const extraExpenses = fetched.filter(e => e && e.expense).map(e => e.expense);
        // Combine: new expenses first, then the rest (excluding duplicates)
        const allNewItems = [
          ...extraExpenses,
          ...fetchedExpenses.filter(e => newExpenseIds.has(e.id))
        ];
        const rest = fetchedExpenses.filter(e => !newExpenseIds.has(e.id));
        setExpenses([...allNewItems, ...rest]);
      } else {
        setExpenses(fetchedExpenses);
      }
      setTotalExpenses(response.total || 0); // Store total count
      setTotalAmount(response.totalAmount || 0); // Store total amount across all filtered results
      setSelectedExpenses(new Set());
      setEditingId(null);
      setExpandedId(null);
    } catch (err) {
      setError(err.message);
      // Log error to backend for monitoring
      logErrorToBackend({
        message: err.message,
        stack: err.stack,
        component: 'ExpenseList',
        action: 'fetchExpenses',
        url: window.location.href
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllExpenses = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        limit: 10000, // Fetch up to 10,000 expenses (backend max)
        offset: 0,
        sortBy,
        sortOrder
      };

      if (categoryFilter !== 'all') {
        filters.category = categoryFilter;
      }
      if (currencyFilter !== 'all') {
        filters.currency = currencyFilter;
      }
      if (dateRange.start) {
        filters.startDate = dateRange.start;
      }
      if (dateRange.end) {
        filters.endDate = dateRange.end;
      }
      if (minAmount) {
        filters.minAmount = parseFloat(minAmount);
      }
      if (maxAmount) {
        filters.maxAmount = parseFloat(maxAmount);
      }
      if (vendorSearch) {
        filters.merchant = vendorSearch;
      }

      const response = await getExpenses(filters);
      const fetchedExpenses = response.expenses || [];

      setExpenses(fetchedExpenses);
      setTotalExpenses(response.total || 0);
      setTotalAmount(response.totalAmount || 0);
      setSelectedExpenses(new Set());
      setEditingId(null);
      setExpandedId(null);
      setShowAllExpenses(true);
    } catch (err) {
      setError(err.message);
      logErrorToBackend({
        message: err.message,
        stack: err.stack,
        component: 'ExpenseList',
        action: 'fetchAllExpenses',
        url: window.location.href
      });
    } finally {
      setLoading(false);
    }
  };

  const sortExpenses = (expenseList) => {
    const sorted = [...expenseList];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'vendor':
          aValue = (a.vendor_name || '').toLowerCase();
          bValue = (b.vendor_name || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const detectDuplicates = (expense) => {
    return expenses.filter(e => {
      if (e.id === expense.id) return false;
      const sameVendor = (e.vendor_name || '').toLowerCase() === (expense.vendor_name || '').toLowerCase();
      const sameAmount = Math.abs((e.amount || 0) - (expense.amount || 0)) < 0.01;
      const sameDate = e.date === expense.date;

      // A duplicate only if vendor name, amount, AND date all match exactly
      return sameVendor && sameAmount && sameDate;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedExpenses(new Set(displayExpenses.map(e => e.id)));
    } else {
      setSelectedExpenses(new Set());
    }
  };

  const handleSelectExpense = (id, event) => {
    const currentIndex = displayExpenses.findIndex(exp => exp.id === id);
    const newSelected = new Set(selectedExpenses);

    // Shift+Click: Select range
    if (event?.shiftKey && lastSelectedIndex !== null && lastSelectedIndex !== currentIndex) {
      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);

      // Select all expenses in the range
      for (let i = start; i <= end; i++) {
        newSelected.add(displayExpenses[i].id);
      }
      setSelectedExpenses(newSelected);
    } else {
      // Normal click: Toggle single selection
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      setSelectedExpenses(newSelected);
      setLastSelectedIndex(currentIndex);
    }
  };

  const loadExpenseDetails = async (expenseId, options = {}) => {
    try {
      setEditLoading(true);
      const response = await getExpenseById(expenseId);
      if (response?.expense) {
        if (options.forEdit) {
          setEditData({
            ...response.expense,
            currency_code: response.expense.currency_code || userDefaultCurrency
          });
        }
        setExpenses(prev => prev.map(item => item.id === response.expense.id ? response.expense : item));
      }
    } catch (error) {
      console.error('Failed to load expense details:', error);
    } finally {
      setEditLoading(false);
    }
  };

  const refreshAfterDelete = (deletedCount) => {
    if (showAllExpenses) {
      fetchAllExpenses();
      return;
    }

    const remainingOnPage = expenses.length - deletedCount;
    if (remainingOnPage <= 0 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
      return;
    }

    fetchExpenses(currentPage);
  };

  const handleBulkDelete = async () => {
    if (selectedExpenses.size === 0) {
      alert('Please select at least one expense to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedExpenses.size} expense(s)? This cannot be undone.`)) {
      return;
    }

    setDeleteLoading('bulk');

    try {
      const ids = Array.from(selectedExpenses);
      await bulkDeleteExpenses(ids);
      const deletedCount = selectedExpenses.size;
      setExpenses(expenses.filter(exp => !selectedExpenses.has(exp.id)));
      setSelectedExpenses(new Set());
      setBulkDeleteMode(false);
      refreshAfterDelete(deletedCount);
    } catch (err) {
      alert('Failed to delete expenses: ' + err.message);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleBulkEdit = async () => {
    if (!bulkEditValue && bulkEditField !== 'category' && bulkEditField !== 'currency_code') {
      alert('Please enter a value');
      return;
    }

    if (!window.confirm(`Update ${bulkEditField} for ${selectedExpenses.size} selected expense(s)?`)) {
      return;
    }

    setBulkEditLoading(true);

    try {
      const ids = Array.from(selectedExpenses);
      let updateCount = 0;
      const updatedExpenses = [...expenses];

      for (const id of ids) {
        const updates = {};

        // Handle different field types
        if (bulkEditField === 'amount') {
          updates.amount = parseFloat(bulkEditValue);
        } else if (bulkEditField === 'date') {
          updates.date = bulkEditValue;
        } else {
          updates[bulkEditField] = bulkEditValue;
        }

        try {
          await updateExpense(id, updates);
          updateCount++;

          // Update local state
          const expenseIndex = updatedExpenses.findIndex(e => e.id === id);
          if (expenseIndex !== -1) {
            updatedExpenses[expenseIndex] = {
              ...updatedExpenses[expenseIndex],
              ...updates
            };
          }
        } catch (error) {
          console.error(`Failed to update expense ${id}:`, error);
        }
      }

      setExpenses(updatedExpenses);
      setSelectedExpenses(new Set());
      setShowBulkEditModal(false);
      setBulkEditField('category');
      setBulkEditValue('');
      alert(`Successfully updated ${updateCount}/${selectedExpenses.size} expense(s)`);
    } catch (err) {
      alert('Failed to bulk edit expenses: ' + err.message);
    } finally {
      setBulkEditLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    setDeleteLoading(id);

    try {
      await deleteExpense(id);
      const deletedCount = 1;
      setExpenses(expenses.filter(exp => exp.id !== id));
      setSelectedExpenses(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      refreshAfterDelete(deletedCount);
    } catch (err) {
      alert('Failed to delete expense: ' + err.message);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleStartEdit = async (expense) => {
    setExpandedId(expense.id);
    setEditingId(expense.id);
    // Set initial data with safe defaults for all required fields
    setEditData({
      vendor_name: expense.vendor_name || '',
      amount: expense.amount !== undefined ? expense.amount : 0,
      date: expense.date || new Date().toISOString().split('T')[0],
      category: expense.category || '',
      description: expense.description || '',
      currency_code: expense.currency_code || userDefaultCurrency,
      line_items: Array.isArray(expense.line_items) ? expense.line_items : [],
      tags: Array.isArray(expense.tags) ? expense.tags : []
    });
    // Load full details from backend asynchronously
    await loadExpenseDetails(expense.id, { forEdit: true });
  };

  const handleViewDetails = async (expense) => {
    const shouldOpen = expandedId !== expense.id;
    setExpandedId(shouldOpen ? expense.id : null);
    if (shouldOpen) {
      await loadExpenseDetails(expense.id);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
    setExpandedId(null);
  };

  const handleCheckDuplicates = () => {
    const duplicatesToSelect = new Set();
    const seenGroups = new Set();

    expenses.forEach(expense => {
      const duplicates = detectDuplicates(expense);
      if (duplicates.length > 0) {
        // Create a unique group identifier (sorted IDs) to avoid processing same group twice
        const groupId = [expense.id, ...duplicates.map(d => d.id)].sort().join(',');

        if (!seenGroups.has(groupId)) {
          seenGroups.add(groupId);

          // Find the original (earliest date) in this group
          const group = [expense, ...duplicates];
          const earliest = group.sort((a, b) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
          )[0];

          // Select all except the earliest (the original)
          group.forEach(exp => {
            if (exp.id !== earliest.id) {
              duplicatesToSelect.add(exp.id);
            }
          });
        }
      }
    });

    if (duplicatesToSelect.size === 0) {
      alert('No duplicates found!');
      return;
    }

    setSelectedExpenses(duplicatesToSelect);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    try {
      if (!editData.vendor_name || !editData.date) {
        alert('Vendor and date are required');
        return;
      }

      const parsedAmount = parseFloat(editData.amount);
      if (Number.isNaN(parsedAmount)) {
        alert('Amount must be a valid number');
        return;
      }

      const updates = {
        vendor_name: editData.vendor_name,
        amount: parsedAmount,
        date: editData.date,
        category: editData.category,
        description: editData.description || null,
        currency_code: editData.currency_code || userDefaultCurrency,
        line_items: Array.isArray(editData.line_items) ? editData.line_items : [],
        tags: Array.isArray(editData.tags) ? editData.tags : [],
      };

      const result = await updateExpense(editingId, updates);

      setExpenses(expenses.map(e => e.id === editingId ? { ...e, ...result.expense } : e));
      setEditingId(null);
      setEditData({});
    } catch (err) {
      alert('Failed to update expense: ' + err.message);
    }
  };

  const handleRemoveDuplicate = (expenseId) => {
    const duplicates = detectDuplicates(expenses.find(e => e.id === expenseId));
    setPotentialDuplicates(duplicates);
    setShowDuplicateWarning(expenseId);
  };

  const confirmRemoveDuplicate = async (duplicateId) => {
    setDeleteLoading(duplicateId);
    try {
      await deleteExpense(duplicateId);
      setExpenses(expenses.filter(exp => exp.id !== duplicateId));
      setPotentialDuplicates(potentialDuplicates.filter(d => d.id !== duplicateId));
    } catch (err) {
      alert('Failed to delete duplicate: ' + err.message);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleExportCSV = () => {
    setExportLoading(true);
    try {
      const filename = getFormattedFilename('csv');
      exportToCSV(displayExpenses, filename);
      setShowExportModal(false);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportPDF = () => {
    setExportLoading(true);
    try {
      const filename = getFormattedFilename('pdf');
      exportToPDF(displayExpenses, filename);
      setShowExportModal(false);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const clearFilters = () => {
    setCategoryFilter('all');
    setSourceFilter('all');
    setTimeRange('current-month');
    setCustomStartDate('');
    setCustomEndDate('');
    setMinAmount('');
    setMaxAmount('');
    setVendorSearch('');
    setCurrentPage(1); // Reset pagination when filters are cleared
  };

  const getCategoryEmoji = (category) => {
    const emojiMap = {
      groceries: '🛒',
      dining: '🍽️',
      transportation: '🚗',
      entertainment: '🎬',
      shopping: '🛍️',
      utilities: '💡',
      healthcare: '🏥',
      other: '📝',
    };
    return emojiMap[category] || '📝';
  };

  const getExpenseSource = (expense) => {
    if (expense.description && expense.description.includes('[Email Forward]')) {
      return 'Email';
    }
    if (expense.receipt_image_url) {
      return 'Upload';
    }
    if (expense.raw_ocr_text && !expense.receipt_image_url) {
      return 'Statement';
    }
    return 'Manual';
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    tableWrapper: {
      overflowX: 'auto',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border-light)',
      marginBottom: 'var(--space-6)',
      boxShadow: 'var(--shadow-sm)',
      backgroundColor: 'var(--color-bg-base)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 'var(--font-size-sm)',
    },
    thead: {
      backgroundColor: 'var(--color-bg-subtle)',
      borderBottom: '2px solid var(--color-border-light)',
    },
    th: {
      padding: isMobile ? 'var(--space-2)' : 'var(--space-3) var(--space-4)',
      textAlign: 'left',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-secondary)',
      fontSize: isMobile ? '10px' : 'var(--font-size-xs)',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '0px' : '0.5px',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    tbody: {},
    tr: {
      borderBottom: '1px solid var(--color-border-light)',
      transition: 'var(--transition-normal)',
    },
    trHover: {
      backgroundColor: 'var(--color-bg-subtle)',
    },
    trSelected: {
      backgroundColor: 'var(--color-success-light)',
    },
    td: {
      padding: isMobile ? 'var(--space-2)' : 'var(--space-3) var(--space-4)',
      color: 'var(--color-text-primary)',
      fontSize: isMobile ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer',
      accentColor: 'var(--color-primary)',
    },
    vendorName: {
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-primary)',
      whiteSpace: isMobile ? 'normal' : 'nowrap',
      wordBreak: isMobile ? 'break-word' : 'normal',
      maxWidth: isMobile ? '120px' : 'none',
    },
    amountPositive: {
      color: 'var(--color-success)',
      fontWeight: 'var(--font-weight-semibold)',
    },
    badge: {
      display: 'inline-block',
      padding: 'var(--space-1) var(--space-3)',
      borderRadius: 'var(--radius-full)',
      fontSize: 'var(--font-size-xs)',
      fontWeight: 'var(--font-weight-semibold)',
      backgroundColor: 'var(--color-info-light)',
      color: 'var(--color-info)',
      whiteSpace: 'nowrap',
    },
    actionButtons: {
      display: 'flex',
      gap: isMobile ? '4px' : 'var(--space-2)',
      justifyContent: 'flex-end',
    },
    iconButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: isMobile ? '14px' : '16px',
      padding: isMobile ? '4px' : 'var(--space-2)',
      borderRadius: 'var(--radius-sm)',
      transition: 'var(--transition-normal)',
      color: 'var(--color-text-secondary)',
    },
    iconButtonHover: {
      backgroundColor: 'var(--color-bg-muted)',
      color: 'var(--color-text-primary)',
    },
  };

  // Apply source filter to expenses
  const filteredExpenses = expenses.filter(expense => {
    if (sourceFilter === 'all') return true;

    const expenseSource = expense.receipt_image_url ? 'upload' :
      (expense.raw_ocr_text && !expense.receipt_image_url) ? 'statement' : 'manual';
    return expenseSource === sourceFilter;
  });

  // Calculate average from total amount across all pages and total count
  const avgAmount = totalExpenses > 0 ? totalAmount / totalExpenses : 0;

  // Client-side re-sort for amount_base to handle NULL amount_in_default_currency
  const displayExpenses = sortBy === 'amount_base'
    ? [...filteredExpenses].sort((a, b) => {
      const aAmt = a.amount_in_default_currency ?? a.amount ?? 0;
      const bAmt = b.amount_in_default_currency ?? b.amount ?? 0;
      return sortOrder === 'desc' ? bAmt - aAmt : aAmt - bAmt;
    })
    : filteredExpenses;

  return (
    <div className="expense-list-container" style={styles.container}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-6)',
        gap: 'var(--space-3)',
        flexWrap: 'wrap'
      }}>
        <h2 style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          margin: 0,
          color: 'var(--color-text-primary)'
        }}>
          Expenses
        </h2>
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)'
        }}>
          <button
            onClick={handleCheckDuplicates}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border-default)',
              backgroundColor: 'var(--color-bg-base)',
              color: 'var(--color-warning)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              cursor: 'pointer',
              transition: 'var(--transition-normal)',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-warning-light)';
              e.target.style.borderColor = 'var(--color-warning)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-bg-base)';
              e.target.style.borderColor = 'var(--color-border-default)';
            }}
          >
            ⚠️ Check Duplicates
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border-default)',
              backgroundColor: showFilters ? 'var(--color-primary)' : 'var(--color-bg-base)',
              color: showFilters ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              cursor: 'pointer',
              transition: 'var(--transition-normal)',
            }}
            onMouseEnter={(e) => {
              if (!showFilters) {
                e.target.style.borderColor = 'var(--color-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showFilters) {
                e.target.style.borderColor = 'var(--color-border-default)';
              }
            }}
          >
            {showFilters ? '✓ Filter' : '🔍 Filter'}
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            disabled={displayExpenses.length === 0}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border-default)',
              backgroundColor: 'var(--color-bg-base)',
              color: displayExpenses.length === 0 ? 'var(--color-text-tertiary)' : 'var(--color-success)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              cursor: displayExpenses.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'var(--transition-normal)',
              opacity: displayExpenses.length === 0 ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (displayExpenses.length > 0) {
                e.target.style.borderColor = 'var(--color-success)';
                e.target.style.backgroundColor = 'var(--color-success-light)';
              }
            }}
            onMouseLeave={(e) => {
              if (displayExpenses.length > 0) {
                e.target.style.borderColor = 'var(--color-border-default)';
                e.target.style.backgroundColor = 'var(--color-bg-base)';
              }
            }}
          >
            📥 Export
          </button>
        </div>
      </div>

      {/* Always visible Vendor search field */}
      <div style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--color-bg-base)',
        border: '1px solid var(--color-border-light)',
        marginBottom: 'var(--space-4)',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div>
          <label style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-secondary)',
            display: 'block',
            marginBottom: 'var(--space-2)'
          }}>
            🔍 Search by Vendor Name
          </label>
          <input
            type="text"
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            placeholder="e.g., Starbucks, Whole Foods, Shell Gas..."
            style={{
              width: '100%',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border-default)',
              fontSize: 'var(--font-size-sm)',
              outline: 'none',
              fontFamily: 'inherit',
              backgroundColor: 'var(--color-bg-base)',
              color: 'var(--color-text-primary)',
              transition: 'var(--transition-normal)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.boxShadow = '0 0 0 2px white, 0 0 0 4px #3B82F6';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--color-border-default)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      {showFilters && (
        <div style={{
          padding: 'var(--space-4)',
          borderRadius: 'var(--radius-lg)',
          backgroundColor: 'var(--color-bg-subtle)',
          border: '1px solid var(--color-border-light)',
          marginBottom: 'var(--space-6)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-3)'
          }}>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#71717A', display: 'block', marginBottom: '4px' }}>
                Category
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #E0E0E0',
                  fontSize: '12px',
                  outline: 'none',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#71717A', display: 'block', marginBottom: '4px' }}>
                Source
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #E0E0E0',
                  fontSize: '12px',
                  outline: 'none',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Sources</option>
                <option value="manual">✍️ Manual</option>
                <option value="upload">📤 Upload</option>
                <option value="statement">🏦 Statement</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#71717A', display: 'block', marginBottom: '4px' }}>
                Currency
              </label>
              <select
                value={currencyFilter}
                onChange={(e) => setCurrencyFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #E0E0E0',
                  fontSize: '12px',
                  outline: 'none',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Currencies</option>
                {currencies.length > 0 ? (
                  currencies.map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.code} - {curr.name}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="SGD">SGD - Singapore Dollar</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="MYR">MYR - Malaysian Ringgit</option>
                    <option value="IDR">IDR - Indonesian Rupiah</option>
                    <option value="EUR">EUR - Euro</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#71717A', display: 'block', marginBottom: '4px' }}>
                Time Range
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #E0E0E0',
                  fontSize: '12px',
                  outline: 'none',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="current-month">This Month</option>
                {availableMonths.map((m) => {
                  const today = new Date();
                  const currentKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                  if (m === currentKey) return null;
                  return <option key={m} value={m}>{formatPeriodLabel(m)}</option>;
                })}
                <option value="this-year">This Year</option>
                <option value="last-year">Last Year</option>
                <option value="all-time">All Time</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#71717A', display: 'block', marginBottom: '4px' }}>
                Min ($)
              </label>
              <input
                type="number"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #E0E0E0',
                  fontSize: '12px',
                  outline: 'none'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#71717A', display: 'block', marginBottom: '4px' }}>
                Max ($)
              </label>
              <input
                type="number"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #E0E0E0',
                  fontSize: '12px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          <button
            onClick={clearFilters}
            style={{
              padding: '6px 12px',
              borderRadius: '4px',
              border: '1px solid #E0E0E0',
              backgroundColor: 'white',
              color: '#71717A',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Clear All
          </button>
        </div>
      )}

      {!loading && totalExpenses > 0 && (
        <div style={{
          padding: '16px',
          borderRadius: '10px',
          backgroundColor: '#f0fdf4',
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: isMobile ? '8px' : '16px',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '10px' : '12px', color: '#71717A', marginBottom: '4px' }}>Total Expenses</div>
            <div style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: '600', letterSpacing: '-0.025em', color: '#1f2937' }}>{formatNumber(totalExpenses, 0)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '10px' : '12px', color: '#71717A', marginBottom: '4px' }}>Total Amount</div>
            <div style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: '600', letterSpacing: '-0.025em', color: '#059669' }}>{formatCurrency(totalAmount, userDefaultCurrency)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '10px' : '12px', color: '#71717A', marginBottom: '4px' }}>Period</div>
            <div style={{ fontSize: isMobile ? '14px' : '20px', fontWeight: '600', letterSpacing: '-0.025em', color: '#1f2937' }}>
              {getTimeRangeLabel()}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px',
          borderRadius: '10px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          marginBottom: '16px',
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#71717A' }}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px',
          }}></div>
          <div>Loading expenses...</div>
        </div>
      ) : displayExpenses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#A1A1AA' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <p style={{ fontSize: '16px', margin: '8px 0' }}>No expenses found</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '10px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
              <input
                type="checkbox"
                checked={selectedExpenses.size === displayExpenses.length && displayExpenses.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={{ fontWeight: '600' }}>
                {selectedExpenses.size > 0 ? `${selectedExpenses.size} selected` : `Select All (${displayExpenses.length})`}
              </span>
            </label>
            {selectedExpenses.size > 0 && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setShowBulkEditModal(true)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '10px',
                    border: '1px solid #E0E0E0',
                    backgroundColor: 'white',
                    color: '#0891b2',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  ✏️ Bulk Edit
                </button>
                <button
                  onClick={() => setSelectedExpenses(new Set())}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '10px',
                    border: '1px solid #E0E0E0',
                    backgroundColor: 'white',
                    color: '#71717A',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Clear Selected
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={deleteLoading === 'bulk'}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: deleteLoading === 'bulk' ? 'not-allowed' : 'pointer',
                    opacity: deleteLoading === 'bulk' ? 0.6 : 1,
                  }}
                >
                  {deleteLoading === 'bulk' ? 'Deleting...' : '🗑️ Delete Selected'}
                </button>
              </div>
            )}
          </div>

          {/* Pagination Controls - Top */}
          {expenses.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              borderBottom: '1px solid var(--color-border-light)',
              backgroundColor: 'var(--color-bg-base)',
            }}>
              <div style={{ fontSize: '14px', color: '#71717A' }}>
                {showAllExpenses ? (
                  `Showing all ${totalExpenses} expenses`
                ) : (
                  `Showing ${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, totalExpenses)} of ${totalExpenses} expenses`
                )}
              </div>
              <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'center'
              }}>
                {!showAllExpenses && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      title="First Page"
                      style={{
                        padding: '8px 10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: currentPage === 1 ? '#F5F5F5' : 'var(--color-bg-base)',
                        color: currentPage === 1 ? '#A1A1AA' : '#374151',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ≪
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: currentPage === 1 ? '#F5F5F5' : 'var(--color-bg-base)',
                        color: currentPage === 1 ? '#A1A1AA' : '#374151',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ← Previous
                    </button>
                    <div style={{
                      display: 'flex',
                      gap: 'var(--space-1)',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontSize: '14px', color: '#71717A' }}>Page</span>
                      <input
                        type="number"
                        min="1"
                        max={Math.ceil(totalExpenses / pageSize) || 1}
                        value={currentPage}
                        onChange={(e) => {
                          const page = parseInt(e.target.value, 10);
                          if (page >= 1 && page <= Math.ceil(totalExpenses / pageSize)) {
                            setCurrentPage(page);
                          }
                        }}
                        style={{
                          width: '50px',
                          padding: '6px',
                          fontSize: '14px',
                          border: '1px solid var(--color-border-light)',
                          borderRadius: '4px',
                          textAlign: 'center'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#71717A' }}>of {Math.ceil(totalExpenses / pageSize) || 1}</span>
                    </div>
                    <button
                      onClick={() => setCurrentPage(Math.min(Math.ceil(totalExpenses / pageSize), currentPage + 1))}
                      disabled={currentPage >= Math.ceil(totalExpenses / pageSize)}
                      style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: currentPage >= Math.ceil(totalExpenses / pageSize) ? '#F5F5F5' : 'var(--color-bg-base)',
                        color: currentPage >= Math.ceil(totalExpenses / pageSize) ? '#A1A1AA' : '#374151',
                        cursor: currentPage >= Math.ceil(totalExpenses / pageSize) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Next →
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.ceil(totalExpenses / pageSize))}
                      disabled={currentPage >= Math.ceil(totalExpenses / pageSize)}
                      title="Last Page"
                      style={{
                        padding: '8px 10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: currentPage >= Math.ceil(totalExpenses / pageSize) ? '#F5F5F5' : 'var(--color-bg-base)',
                        color: currentPage >= Math.ceil(totalExpenses / pageSize) ? '#A1A1AA' : '#374151',
                        cursor: currentPage >= Math.ceil(totalExpenses / pageSize) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ≫
                    </button>
                    <button
                      onClick={fetchAllExpenses}
                      style={{
                        padding: '8px 14px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: 'var(--color-bg-base)',
                        color: '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Show all
                    </button>
                  </>
                )}
                {showAllExpenses && (
                  <button
                    onClick={() => {
                      setSourceFilter('all');
                      setShowAllExpenses(false);
                      setCurrentPage(1);
                      fetchExpenses(1);
                    }}
                    style={{
                      padding: '8px 14px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '10px',
                      backgroundColor: 'var(--color-bg-base)',
                      color: '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Back to Pagination
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  <th style={{ ...styles.th, width: '40px', cursor: 'default' }}>
                    <input
                      type="checkbox"
                      checked={selectedExpenses.size === displayExpenses.length && displayExpenses.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={styles.checkbox}
                    />
                  </th>
                  <th
                    style={{ ...styles.th, color: sortBy === 'vendor' ? '#3B82F6' : undefined }}
                    onClick={() => {
                      if (sortBy === 'vendor') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy('vendor');
                        setSortOrder('asc');
                      }
                    }}
                  >
                    Name {sortBy === 'vendor' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th
                    style={{ ...styles.th, textAlign: 'right', color: sortBy === 'amount_base' ? '#3B82F6' : undefined }}
                    onClick={() => {
                      if (sortBy === 'amount_base') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy('amount_base');
                        setSortOrder('desc');
                      }
                    }}
                  >
                    Amount {sortBy === 'amount_base' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th
                    style={{ ...styles.th, color: sortBy === 'date' ? '#3B82F6' : undefined }}
                    onClick={() => {
                      if (sortBy === 'date') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy('date');
                        setSortOrder('desc');
                      }
                    }}
                  >
                    Date {sortBy === 'date' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  {!isMobile && <th style={{ ...styles.th, cursor: 'default' }}>Category</th>}
                  {!isMobile && <th style={{ ...styles.th, cursor: 'default' }}>Source</th>}
                  <th style={{ ...styles.th, textAlign: 'center', width: isMobile ? '80px' : '120px', cursor: 'default' }}>Actions</th>
                </tr>
              </thead>
              <tbody style={styles.tbody}>
                {displayExpenses.map((expense, index) => (
                  <tr
                    key={expense.id}
                    style={{
                      ...styles.tr,
                      ...(index % 2 === 0 ? { backgroundColor: '#fafafa' } : {}),
                      ...(selectedExpenses.has(expense.id) ? styles.trSelected : {}),
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedExpenses.has(expense.id)) {
                        e.currentTarget.style.backgroundColor = '#F5F5F5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedExpenses.has(expense.id)) {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fafafa' : 'white';
                      }
                    }}
                  >
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedExpenses.has(expense.id)}
                        onClick={(e) => handleSelectExpense(expense.id, e)}
                        onChange={() => { }} // Handled by onClick for shift key detection
                        style={styles.checkbox}
                      />
                    </td>
                    <td style={{ ...styles.td, ...styles.vendorName }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          onClick={() => handleViewDetails(expense)}
                          style={{ cursor: 'pointer', color: '#2563eb' }}
                          onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {expense.vendor_name || 'Unknown'}
                        </span>
                        {newExpenseIds.has(expense.id) && (
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '700',
                            backgroundColor: '#22c55e',
                            color: 'white',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            animation: 'pulse 2s infinite'
                          }}>
                            NEW
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...styles.td, ...styles.amountPositive, textAlign: 'right' }}>
                      <div>
                        {expense.currency_code && expense.currency_code !== userDefaultCurrency ? (
                          isMobile ? (
                            formatCurrency(expense.amount_in_default_currency ?? expense.amount, userDefaultCurrency)
                          ) : (
                            <>
                              <div>{expense.currency_code} {formatNumber(expense.amount)}</div>
                              <div style={{ fontSize: '0.85em', color: '#999', marginTop: '2px' }}>
                                ≈ {formatCurrency(expense.amount_in_default_currency ?? expense.amount, userDefaultCurrency)}
                              </div>
                            </>
                          )
                        ) : (
                          formatCurrency(expense.amount, userDefaultCurrency)
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      {isMobile ? (() => {
                        const d = new Date(expense.date + 'T00:00:00');
                        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        return `${d.getDate()}-${months[d.getMonth()]}`;
                      })() : formatDateDisplay(expense.date)}
                    </td>
                    {!isMobile && (
                      <td style={styles.td}>
                        <span style={styles.badge}>
                          {getCategoryEmoji(expense.category)} {expense.category}
                        </span>
                      </td>
                    )}
                    {!isMobile && (
                      <td style={styles.td}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '14px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: getExpenseSource(expense) === 'Upload' ? '#dbeafe' : getExpenseSource(expense) === 'Statement' ? '#fce7f3' : getExpenseSource(expense) === 'Email' ? '#fef3c7' : '#e0e7ff',
                          color: getExpenseSource(expense) === 'Upload' ? '#1e40af' : getExpenseSource(expense) === 'Statement' ? '#831843' : getExpenseSource(expense) === 'Email' ? '#92400e' : '#4f46e5',
                          whiteSpace: 'nowrap',
                        }}>
                          {getExpenseSource(expense) === 'Upload' && '📤'} {getExpenseSource(expense) === 'Statement' && '🏦'} {getExpenseSource(expense) === 'Email' && '📧'} {getExpenseSource(expense) === 'Manual' && '✍️'} {getExpenseSource(expense)}
                        </span>
                      </td>
                    )}
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={styles.actionButtons}>
                        <button
                          onClick={() => handleViewDetails(expense)}
                          style={styles.iconButton}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => handleStartEdit(expense)}
                          style={styles.iconButton}
                          title="Edit"
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          disabled={deleteLoading === expense.id}
                          style={{ ...styles.iconButton, color: deleteLoading === expense.id ? '#999' : '#dc2626' }}
                          title="Delete"
                        >
                          {deleteLoading === expense.id ? '⏳' : '🗑️'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls - Bottom */}
          {expenses.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              borderTop: '1px solid var(--color-border-light)',
              backgroundColor: 'var(--color-bg-base)',
            }}>
              <div style={{ fontSize: '14px', color: '#71717A' }}>
                {showAllExpenses ? (
                  `Showing all ${totalExpenses} expenses`
                ) : (
                  `Showing ${((currentPage - 1) * pageSize) + 1} - ${Math.min(currentPage * pageSize, totalExpenses)} of ${totalExpenses} expenses`
                )}
              </div>
              <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'center'
              }}>
                {!showAllExpenses && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      title="First Page"
                      style={{
                        padding: '8px 10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: currentPage === 1 ? '#F5F5F5' : 'var(--color-bg-base)',
                        color: currentPage === 1 ? '#A1A1AA' : '#374151',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ≪
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: currentPage === 1 ? '#F5F5F5' : 'var(--color-bg-base)',
                        color: currentPage === 1 ? '#A1A1AA' : '#374151',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ← Previous
                    </button>
                    <div style={{
                      display: 'flex',
                      gap: 'var(--space-1)',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontSize: '14px', color: '#71717A' }}>Page</span>
                      <input
                        type="number"
                        min="1"
                        max={Math.ceil(totalExpenses / pageSize) || 1}
                        value={currentPage}
                        onChange={(e) => {
                          const page = parseInt(e.target.value, 10);
                          if (page >= 1 && page <= Math.ceil(totalExpenses / pageSize)) {
                            setCurrentPage(page);
                          }
                        }}
                        style={{
                          width: '50px',
                          padding: '6px',
                          fontSize: '14px',
                          border: '1px solid var(--color-border-light)',
                          borderRadius: '4px',
                          textAlign: 'center'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#71717A' }}>of {Math.ceil(totalExpenses / pageSize) || 1}</span>
                    </div>
                    <button
                      onClick={() => setCurrentPage(Math.min(Math.ceil(totalExpenses / pageSize), currentPage + 1))}
                      disabled={currentPage >= Math.ceil(totalExpenses / pageSize)}
                      style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: currentPage >= Math.ceil(totalExpenses / pageSize) ? '#F5F5F5' : 'var(--color-bg-base)',
                        color: currentPage >= Math.ceil(totalExpenses / pageSize) ? '#A1A1AA' : '#374151',
                        cursor: currentPage >= Math.ceil(totalExpenses / pageSize) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Next →
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.ceil(totalExpenses / pageSize))}
                      disabled={currentPage >= Math.ceil(totalExpenses / pageSize)}
                      title="Last Page"
                      style={{
                        padding: '8px 10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: currentPage >= Math.ceil(totalExpenses / pageSize) ? '#F5F5F5' : 'var(--color-bg-base)',
                        color: currentPage >= Math.ceil(totalExpenses / pageSize) ? '#A1A1AA' : '#374151',
                        cursor: currentPage >= Math.ceil(totalExpenses / pageSize) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ≫
                    </button>
                    <button
                      onClick={fetchAllExpenses}
                      style={{
                        padding: '8px 14px',
                        fontSize: '14px',
                        fontWeight: '600',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '10px',
                        backgroundColor: 'var(--color-bg-base)',
                        color: '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Show all
                    </button>
                  </>
                )}
                {showAllExpenses && (
                  <button
                    onClick={() => {
                      setSourceFilter('all');
                      setShowAllExpenses(false);
                      setCurrentPage(1);
                      fetchExpenses(1);
                    }}
                    style={{
                      padding: '8px 14px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '10px',
                      backgroundColor: 'var(--color-bg-base)',
                      color: '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Back to Pagination
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Expanded Detail View Modal */}
      {expandedId && expenses.find(e => e.id === expandedId) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}>
            {editingId === expandedId ? (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1f2937', fontWeight: '600', letterSpacing: '-0.025em' }}>Edit Expense</h3>
                <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#71717A', marginBottom: '4px' }}>
                      Vendor
                    </label>
                    <input
                      type="text"
                      value={editData.vendor_name ?? ''}
                      onChange={(e) => setEditData({ ...editData, vendor_name: e.target.value })}
                      disabled={editLoading}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #E0E0E0',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#71717A', marginBottom: '4px' }}>
                      Amount
                    </label>
                    <input
                      type="number"
                      value={editData.amount ?? ''}
                      onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                      step="0.01"
                      min="0"
                      disabled={editLoading}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #E0E0E0',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#71717A', marginBottom: '4px' }}>
                      Date
                    </label>
                    <input
                      type="date"
                      value={editData.date ?? ''}
                      onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                      disabled={editLoading}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #E0E0E0',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#71717A', marginBottom: '4px' }}>
                      Category
                    </label>
                    <select
                      value={editData.category ?? ''}
                      onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                      disabled={editLoading}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #E0E0E0',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    >
                      {categories.filter(c => c !== 'all').map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#71717A', marginBottom: '4px' }}>
                      Currency
                    </label>
                    <select
                      value={editData.currency_code ?? 'SGD'}
                      onChange={(e) => setEditData({ ...editData, currency_code: e.target.value })}
                      disabled={editLoading}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #E0E0E0',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    >
                      {currencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#71717A', marginBottom: '4px' }}>
                      Description
                    </label>
                    <textarea
                      value={editData.description ?? ''}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      disabled={editLoading}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #E0E0E0',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                        minHeight: '60px',
                        resize: 'vertical',
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleSaveEdit}
                    disabled={editLoading}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: editLoading ? '#A1A1AA' : '#22c55e',
                      color: 'white',
                      fontWeight: '600',
                      cursor: editLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {editLoading ? 'Loading...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '10px',
                      border: '1px solid #E0E0E0',
                      backgroundColor: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px', fontWeight: '600', letterSpacing: '-0.025em' }}>
                    {expenses.find(e => e.id === expandedId)?.vendor_name || 'Expense Details'}
                  </h3>
                  <button
                    onClick={() => setExpandedId(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      cursor: 'pointer',
                      padding: '0',
                    }}
                  >
                    ✕
                  </button>
                </div>

                {(() => {
                  const exp = expenses.find(e => e.id === expandedId);
                  return (
                    <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#71717A' }}>Amount:</span>
                        <span style={{ color: '#1f2937', marginLeft: '8px', fontWeight: '700' }}>
                          {exp?.currency_code && exp.currency_code !== userDefaultCurrency
                            ? `${exp.currency_code} ${formatNumber(exp?.amount)}`
                            : formatCurrency(exp?.amount, userDefaultCurrency)}
                        </span>
                        {exp?.currency_code && exp.currency_code !== userDefaultCurrency && (
                          <div style={{ marginTop: '6px', color: '#71717A', fontSize: '12px' }}>
                            ≈ {formatCurrency(exp?.amount_in_default_currency ?? exp?.amount, userDefaultCurrency)}
                          </div>
                        )}
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#71717A' }}>Date:</span>
                        <span style={{ color: '#1f2937', marginLeft: '8px' }}>
                          {formatDateDisplay(exp?.date)}
                        </span>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#71717A' }}>Category:</span>
                        <span style={{ marginLeft: '8px' }}>
                          <span style={styles.badge}>{getCategoryEmoji(exp?.category)} {exp?.category}</span>
                        </span>
                      </div>
                      {exp?.description && (
                        <div style={{ marginBottom: '12px' }}>
                          <span style={{ fontWeight: '600', color: '#71717A' }}>Description:</span>
                          <div style={{ color: '#374151', marginTop: '4px', padding: '8px', backgroundColor: '#F5F5F5', borderRadius: '4px' }}>
                            {exp.description}
                          </div>
                        </div>
                      )}

                      {exp?.line_items && exp.line_items.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <span style={{ fontWeight: '600', color: '#71717A', display: 'block', marginBottom: '8px' }}>Items:</span>
                          <div style={{ paddingLeft: '12px', borderLeft: '2px solid #E8E8E8' }}>
                            {exp.line_items.map((item, i) => (
                              <div key={i} style={{ marginBottom: '6px', color: '#374151' }}>
                                {item.name} {item.quantity && `× ${item.quantity}`} <span style={{ color: '#059669', fontWeight: '600' }}>{formatCurrency(item.price, exp?.currency_code || userDefaultCurrency)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {exp?.tags && exp.tags.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <span style={{ fontWeight: '600', color: '#71717A' }}>Tags:</span>
                          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {exp.tags.map((tag, i) => (
                              <span key={i} style={{
                                padding: '4px 8px',
                                backgroundColor: '#e0e7ff',
                                color: '#4f46e5',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Receipt Image Section */}
                      {exp?.receipt_image_url && (
                        <div style={{ marginTop: '16px', marginBottom: '12px' }}>
                          <span style={{ fontWeight: '600', color: '#71717A', display: 'block', marginBottom: '8px' }}>
                            📷 Receipt Image:
                          </span>
                          {receiptImageLoading ? (
                            <div style={{
                              padding: '40px',
                              textAlign: 'center',
                              backgroundColor: '#F5F5F5',
                              borderRadius: '10px',
                              color: '#71717A'
                            }}>
                              Loading receipt image...
                            </div>
                          ) : receiptImageUrl ? (
                            <div style={{
                              borderRadius: '10px',
                              overflow: 'hidden',
                              border: '1px solid #E8E8E8',
                              backgroundColor: '#FAFAFA'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden',
                                backgroundColor: '#F5F5F5'
                              }}>
                                <img
                                  src={receiptImageUrl}
                                  alt="Receipt"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '400px',
                                    objectFit: 'contain',
                                    display: 'block',
                                    transform: `rotate(${imageRotation}deg)`,
                                    transition: 'transform 0.3s ease'
                                  }}
                                  onClick={() => window.open(receiptImageUrl, '_blank')}
                                  title="Click to view full size"
                                />
                              </div>
                              {/* Rotation Controls */}
                              <div style={{
                                padding: '12px',
                                borderTop: '1px solid #E8E8E8',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '8px',
                                flexWrap: 'wrap'
                              }}>
                                <button
                                  onClick={() => handleRotateImage('ccw')}
                                  disabled={isSavingRotation}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#F5F5F5',
                                    border: '1px solid #E0E0E0',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  title="Rotate left 90°"
                                >
                                  ↺ Left
                                </button>
                                <button
                                  onClick={() => handleRotateImage('cw')}
                                  disabled={isSavingRotation}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#F5F5F5',
                                    border: '1px solid #E0E0E0',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  title="Rotate right 90°"
                                >
                                  ↻ Right
                                </button>
                                {imageRotation !== 0 && (
                                  <button
                                    onClick={handleSaveRotation}
                                    disabled={isSavingRotation}
                                    style={{
                                      padding: '6px 16px',
                                      backgroundColor: isSavingRotation ? '#A1A1AA' : '#3b82f6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '10px',
                                      cursor: isSavingRotation ? 'not-allowed' : 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                    title="Save rotated image permanently"
                                  >
                                    {isSavingRotation ? '💾 Saving...' : '💾 Save Image'}
                                  </button>
                                )}
                              </div>
                              <div style={{
                                padding: '8px',
                                textAlign: 'center',
                                fontSize: '11px',
                                color: '#A1A1AA',
                                borderTop: '1px solid #E8E8E8'
                              }}>
                                Click image to view full size
                              </div>
                            </div>
                          ) : (
                            <div style={{
                              padding: '20px',
                              textAlign: 'center',
                              backgroundColor: '#fef2f2',
                              borderRadius: '10px',
                              color: '#991b1b',
                              fontSize: '12px'
                            }}>
                              Unable to load receipt image
                            </div>
                          )}
                        </div>
                      )}

                      {detectDuplicates(exp).length > 0 && (
                        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '10px', border: '1px solid #fcd34d' }}>
                          <button
                            onClick={() => handleRemoveDuplicate(exp.id)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            ⚠️ {detectDuplicates(exp).length} Duplicate(s) Found
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      // Find the expanded expense and populate editData
                      const expenseToEdit = expenses.find(e => e.id === expandedId);
                      if (expenseToEdit) {
                        setEditingId(expandedId);
                        setEditData({
                          vendor_name: expenseToEdit.vendor_name || '',
                          amount: expenseToEdit.amount !== undefined ? expenseToEdit.amount : 0,
                          date: expenseToEdit.date || new Date().toISOString().split('T')[0],
                          category: expenseToEdit.category || '',
                          description: expenseToEdit.description || '',
                          currency_code: expenseToEdit.currency_code || userDefaultCurrency,
                          line_items: Array.isArray(expenseToEdit.line_items) ? expenseToEdit.line_items : [],
                          tags: Array.isArray(expenseToEdit.tags) ? expenseToEdit.tags : []
                        });
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '10px',
                      border: '1px solid #bfdbfe',
                      backgroundColor: 'white',
                      color: '#3b82f6',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    ✎ Edit
                  </button>
                  <button
                    onClick={() => setExpandedId(null)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '10px',
                      border: '1px solid #E0E0E0',
                      backgroundColor: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}

            {showDuplicateWarning === expandedId && potentialDuplicates.length > 0 && (
              <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '10px',
              }}>
                <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '12px' }}>
                  Found {potentialDuplicates.length} possible duplicate(s):
                </div>
                {potentialDuplicates.map((dup) => (
                  <div key={dup.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontSize: '12px' }}>
                    <input
                      type="checkbox"
                      defaultChecked
                      id={`dup-${dup.id}`}
                    />
                    <label htmlFor={`dup-${dup.id}`} style={{ flex: 1 }}>
                      {dup.vendor_name} - {formatCurrency(dup.amount, dup.currency_code || userDefaultCurrency)} ({formatDateDisplay(dup.date)})
                    </label>
                    <button
                      onClick={() => {
                        const checkbox = document.getElementById(`dup-${dup.id}`);
                        if (checkbox.checked) {
                          confirmRemoveDuplicate(dup.id);
                        }
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                      disabled={deleteLoading === dup.id}
                    >
                      {deleteLoading === dup.id ? '...' : 'Remove'}
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setShowDuplicateWarning(null)}
                  style={{
                    marginTop: '8px',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: '1px solid #E0E0E0',
                    backgroundColor: 'white',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '14px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}>
            <h2 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '18px', fontWeight: '600', letterSpacing: '-0.025em' }}>
              ✏️ Bulk Edit {selectedExpenses.size} Expense(s)
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#71717A', marginBottom: '6px' }}>
                Field to Update
              </label>
              <select
                value={bulkEditField}
                onChange={(e) => {
                  setBulkEditField(e.target.value);
                  setBulkEditValue('');
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '10px',
                  border: '1px solid #E0E0E0',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                <option value="vendor_name">Vendor Name</option>
                <option value="amount">Amount</option>
                <option value="date">Date</option>
                <option value="category">Category</option>
                <option value="currency_code">Currency</option>
                <option value="description">Description</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#71717A', marginBottom: '6px' }}>
                New Value
              </label>
              {bulkEditField === 'category' ? (
                <select
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '10px',
                    border: '1px solid #E0E0E0',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="">-- Select a category --</option>
                  {categories.filter(c => c !== 'all').map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              ) : bulkEditField === 'date' ? (
                <input
                  type="date"
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '10px',
                    border: '1px solid #E0E0E0',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              ) : bulkEditField === 'amount' ? (
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '10px',
                    border: '1px solid #E0E0E0',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              ) : bulkEditField === 'currency_code' ? (
                <select
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '10px',
                    border: '1px solid #E0E0E0',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="">-- Select a currency --</option>
                  {currencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              ) : bulkEditField === 'description' ? (
                <textarea
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="Enter description"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '10px',
                    border: '1px solid #E0E0E0',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    minHeight: '80px',
                    resize: 'vertical',
                  }}
                />
              ) : (
                <input
                  type="text"
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="Enter value"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '10px',
                    border: '1px solid #E0E0E0',
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              )}
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#F5F5F5',
              borderRadius: '10px',
              marginBottom: '20px',
              fontSize: '12px',
              color: '#71717A',
            }}>
              This will update the <strong>{bulkEditField}</strong> field for <strong>{selectedExpenses.size}</strong> selected expense(s).
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleBulkEdit}
                disabled={bulkEditLoading}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: '#0891b2',
                  color: 'white',
                  fontWeight: '600',
                  cursor: bulkEditLoading ? 'not-allowed' : 'pointer',
                  opacity: bulkEditLoading ? 0.6 : 1,
                }}
              >
                {bulkEditLoading ? 'Updating...' : 'Confirm Update'}
              </button>
              <button
                onClick={() => {
                  setShowBulkEditModal(false);
                  setBulkEditField('category');
                  setBulkEditValue('');
                }}
                disabled={bulkEditLoading}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid #E0E0E0',
                  backgroundColor: 'white',
                  fontWeight: '600',
                  cursor: bulkEditLoading ? 'not-allowed' : 'pointer',
                  opacity: bulkEditLoading ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--color-bg-base)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '90%',
            boxShadow: 'var(--shadow-lg)',
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-bold)',
              marginTop: 0,
              marginBottom: 'var(--space-2)',
              color: 'var(--color-text-primary)',
            }}>
              📥 Export Expenses
            </h3>
            <p style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--space-4)',
            }}>
              Select a format to export {displayExpenses.length} expense{displayExpenses.length !== 1 ? 's' : ''}
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
            }}>
              <button
                onClick={handleExportCSV}
                disabled={exportLoading}
                style={{
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: exportLoading ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-normal)',
                  opacity: exportLoading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!exportLoading) {
                    e.target.style.backgroundColor = 'var(--color-primary-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!exportLoading) {
                    e.target.style.backgroundColor = 'var(--color-primary)';
                  }
                }}
              >
                {exportLoading ? '⏳ Exporting...' : '📄 CSV'}
              </button>

              <button
                onClick={handleExportPDF}
                disabled={exportLoading}
                style={{
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: exportLoading ? 'not-allowed' : 'pointer',
                  transition: 'var(--transition-normal)',
                  opacity: exportLoading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!exportLoading) {
                    e.target.style.backgroundColor = 'var(--color-primary-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!exportLoading) {
                    e.target.style.backgroundColor = 'var(--color-primary)';
                  }
                }}
              >
                {exportLoading ? '⏳ Exporting...' : '📕 PDF'}
              </button>
            </div>

            <button
              onClick={() => setShowExportModal(false)}
              disabled={exportLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border-default)',
                backgroundColor: 'var(--color-bg-muted)',
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: exportLoading ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-normal)',
                opacity: exportLoading ? 0.5 : 1,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        @media (max-width: 768px) {
          .expense-list-container {
            padding: 8px !important;
            max-width: 100% !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          table {
            font-size: 11px !important;
            table-layout: fixed;
            width: 100% !important;
          }

          th, td {
            padding: 6px 4px !important;
            white-space: nowrap;
          }

          /* Checkbox column */
          th:nth-child(1),
          td:nth-child(1) {
            width: 28px !important;
          }

          /* Name column - takes most space, truncates if needed */
          th:nth-child(2),
          td:nth-child(2) {
            overflow: hidden;
            text-overflow: ellipsis;
          }

          /* Amount column - fixed width, always fully visible */
          th:nth-child(3),
          td:nth-child(3) {
            width: 72px !important;
          }

          /* Date column - fixed width, always fully visible */
          th:nth-child(4),
          td:nth-child(4) {
            width: 46px !important;
          }

          /* Hide Category (5th), Source (6th), Actions (7th) on mobile */
          th:nth-child(5),
          td:nth-child(5),
          th:nth-child(6),
          td:nth-child(6),
          th:nth-child(7),
          td:nth-child(7) {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .expense-list-container {
            padding: 4px !important;
          }

          table {
            font-size: 10px !important;
          }

          th, td {
            padding: 4px 3px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ExpenseList;
